/*
 * Copyright (c) 2020 Honeywell International Inc. All Rights Reserved.
 * Honeywell and its product names are among the trademarks and/or service marks
 * owned by Honeywell International, Inc., or its subsidiaries.
 *
 * For patent information, see http://www.hsmpats.com.
 */

import React, { Component } from 'react';
import { Button, Card, Grid, Icon, Progress } from '@scuf/common';
import { DataTable } from '@scuf/datatable';
import moment from 'moment';
import { AppContext } from '@keystone/common';
import APIClient from '../../common/APIClient';

class TaskHistory extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tableData: [],
      downloading: false,
      downloadFilename: '',
      downloadPercent: 0,
    };
  }

  componentDidMount() {
    const { registerReloadFunction } = this.context;
    registerReloadFunction(this.getTableData);
    this.getTableData();
  }

  componentWillUnmount() {
    const { unregisterReloadFunction, setNotification } = this.context;
    unregisterReloadFunction();
    setNotification(null, null);
  }

  getTableData = async () => {
    const { user, handleError } = this.context;
    try {
      const data = await APIClient.fetchTaskHistory(user);
      this.setState({ tableData: data.response });
    } catch (err) {
      handleError(err);
    }
  };

  setDownloading(fileName) {
    this.setState({
      downloading: true,
      downloadFilename: fileName,
    });
  }

  updateValue = (key, value) => {
    this.setState({ [key]: value });
  };

  clearDownloading() {
    this.setState({
      downloading: false,
      downloadFilename: '',
      downloadPercent: 0,
    });
  }

  renderStatus = (cellData) => {
    if (cellData.value === 'success') {
      return (
        <>
          <Icon root="common" name="badge-check" size="medium" color="green" />
          {/* prettier-ignore */}
          <>  Success</>
        </>
      );
    }
    if (cellData.value === 'error') {
      return (
        <>
          <Icon root="common" name="badge-warning" size="medium" color="red" />
          {/* prettier-ignore */}
          <>  Error</>
        </>
      );
    }
    if (cellData.value === 'executing') {
      return (
        <>
          <Icon root="global" name="refresh" size="medium" loading />
          {/* prettier-ignore */}
          <>  In Progress</>
        </>
      );
    }
    return <Icon root="common" name="badge-help" size="medium" color="blue" />;
  };

  renderDownloadLink = (cellData) => {
    const { user, setNotification } = this.context;
    const { downloading } = this.state;
    return (
      <Button
        type="link"
        size="small"
        icon={<Icon name="file-download" root="global" size="medium" />}
        content="Download"
        iconPosition="left"
        disabled={downloading || cellData.rowData.status !== 'success' || cellData.rowData.deleted}
        onClick={async () => {
          this.setDownloading(cellData.rowData.projectFileName);
          try {
            await APIClient.downloadTaskFileAxios(
              cellData.rowData.projectFileDir,
              cellData.rowData.projectFileName,
              user,
              this.updateValue
            );
            setNotification('Project downloaded successfully', 'success');
            APIClient.deleteTaskConversionProject(cellData.rowData.id, user);
          } catch (err) {
            setNotification('An error occured', 'critical');
          }
          this.clearDownloading();
        }}
      />
    );
  };

  renderDownloadProgress() {
    const { downloadFilename, downloadPercent } = this.state;
    return (
      <Grid.Row>
        <Card>
          <Card.Header title="Download Progress" />
          <Card.Content>
            {downloadFilename}
            <Progress percent={downloadPercent} size="xlarge" />
          </Card.Content>
        </Card>
      </Grid.Row>
    );
  }

  render() {
    const { tableData, downloading } = this.state;
    return (
      <Grid>
        {downloading ? this.renderDownloadProgress() : <></>}
        <Grid.Row>
          <Card>
            <Card.Header title="Task Upload History" />
            <Card.Content>
              <DataTable data={tableData}>
                <DataTable.Column field="taskName" header="Task File" />
                <DataTable.Column
                  field="status"
                  header="Status"
                  renderer={(cellData) => this.renderStatus(cellData)}
                />
                <DataTable.Column field="projectFileName" header="Project" />
                <DataTable.Column
                  field=""
                  header="Download"
                  renderer={(cellData) => this.renderDownloadLink(cellData)}
                />
                <DataTable.Column
                  field="date"
                  header="Upload Date"
                  renderer={(cellData) => moment(cellData.value).format('lll')}
                />
              </DataTable>
            </Card.Content>
            <Card.Footer align="left">
              <b>Note:</b>&nbsp;Converted DevKit projects can only be downloaded once.
            </Card.Footer>
          </Card>
        </Grid.Row>
      </Grid>
    );
  }
}

TaskHistory.contextType = AppContext;

export default TaskHistory;
